import React, { useCallback } from 'react';
import { applicationPaths } from '@core/http/applicationPaths';
import httpClient from '@core/http/httpClient';
import { t } from '@lingui/macro';
import { AccountModel } from '@shared/models';
import { isEmpty } from 'lodash';
import { AccountsTreeList } from '@hwm/ui-lib';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores/selectors';
import { accountsReducer } from '@core/store/library-stores/accounts';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';
import { unwrapResult } from '@reduxjs/toolkit';

export const AccountHierarchy: React.FC = () => {
  const dispatch = useAppDispatch();

  const { selectedAccount, subaccounts, hierarchy, loading } = useAppSelector(selectedAccStateSelector);

  const onAccountSelected = useCallback(
    (account: AccountModel) => {
      dispatch(accountsReducer.slice.actions.setSelectedAccount({ account: account }));
    },
    [dispatch]
  );

  const setHierarchyAction = (accounts: AccountModel[]) =>
    dispatch(accountsReducer.slice.actions.setHierarchy({ hierarchy: accounts }));

  return (
    <div className="flex flex-col h-full w-full overflow-hidden" data-testid="sidebar-accounts-tree-list-wrapper">
      <AccountsTreeList
        dataTestId="sidebar-accounts-tree-list"
        onAccountSelected={onAccountSelected}
        selectedAccount={selectedAccount}
        subaccounts={subaccounts}
        hierarchy={hierarchy}
        setHierarchy={setHierarchyAction}
        fetchSubaccounts={(account) =>
          dispatch(
            accountsThunks.fetchSubAccounts({
              accountId: account ? (account.id || account['accountId']).toString() : null,
            })
          )
        }
        loading={loading}
        autocompleteProps={{
          newEditableLook: true,
          placeholder: t`Search account`,
          getResults: (searchQuery: string) =>
            httpClient
              .get<AccountModel[]>(applicationPaths.accounts.getAccAutocomplete, {
                params: {
                  name: searchQuery,
                },
              })
              .then((result) => {
                return result.data && !isEmpty(result.data) ? result.data : [];
              }),
          optionSelected: (account) => {
            dispatch(
              accountsThunks.fetchById({
                accountId: (account?.id || account?.['accountId'])?.toString(),
              })
            )
              .then(unwrapResult)
              .then((account) => onAccountSelected(account));
          },
        }}
      />
    </div>
  );
};
